/**
 * The layout of a `campaignStructure` grid's rows as a single CSS grid, so a
 * cell can span several rows (`rowSpan`) as well as several columns
 * (`colSpan`).
 *
 * Every row keeps the equal-width columns it would have on its own — its
 * cells' spans add up to its column count, or `columns` fixes it — so the grid
 * is divided into the finest sub-columns all of the rows need, and each cell's
 * position and span are scaled to them.
 *
 * A cell spanning rows keeps the position and width it has in its first row.
 * The rows below count its columns as taken: their own cells share the rest of
 * the width, left to right around it. A row whose cells do not fit wraps onto
 * extra lines, which the spanning cells above extend through — `rowSpan` counts
 * rows, not lines.
 *
 * Pure (no React), so it can be checked on its own.
 */

/** A row has at most this many columns. */
export const MAX_ROW_COLUMNS = 5;

const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));

const lcm = (a, b) => (a / gcd(a, b)) * b;

// Exact fractions of the grid's width, `[numerator, denominator]`.
const frac = (n, d = 1) => {
  const g = gcd(n, d) || 1;

  return [n / g, d / g];
};

const add = ([an, ad], [bn, bd]) => frac(an * bd + bn * ad, ad * bd);
const sub = ([an, ad], [bn, bd]) => frac(an * bd - bn * ad, ad * bd);
const mul = ([an, ad], k) => frac(an * k, ad);
const div = ([an, ad], k) => frac(an, ad * k);
const cmp = ([an, ad], [bn, bd]) => an * bd - bn * ad;

const ZERO = frac(0);
const ONE = frac(1);

/** A cell's span from its `key` (`colSpan`, `rowSpan`): 1 by default. */
const getSpan = (cell, key) =>
  Number.isInteger(cell?.[key]) && cell[key] > 1 ? cell[key] : 1;

/** How many columns a cell spans: its `colSpan`, 1 by default. */
export const getCellColSpan = (cell) => getSpan(cell, "colSpan");

/** How many rows a cell spans: its `rowSpan`, 1 by default. */
export const getCellRowSpan = (cell) => getSpan(cell, "rowSpan");

/** The free stretches of a line around the `taken` ones, left to right. */
const getFreeSegments = (taken) => {
  const segments = [];
  let cursor = ZERO;

  [...taken]
    .sort((a, b) => cmp(a.start, b.start))
    .forEach(({ start, width }) => {
      if (cmp(start, cursor) > 0) {
        segments.push({ start: cursor, end: start });
      }

      const end = add(start, width);

      if (cmp(end, cursor) > 0) {
        cursor = end;
      }
    });

  if (cmp(ONE, cursor) > 0) {
    segments.push({ start: cursor, end: ONE });
  }

  return segments;
};

/**
 * Lays out a grid's `rows` (lists of cells, `null` for an empty column).
 *
 * @param {Array<Array<object|null>>} rows
 * @param {object} [options]
 * @param {number} [options.columns] - Fixes every row's column count.
 * @param {boolean} [options.fillLines] - Stretches the last cell of every line
 *   over what is left of the free stretch it sits in — unless it is a `null`
 *   placeholder, whose empty columns are deliberate.
 * @returns {{
 *   columns: number,
 *   lines: number,
 *   cells: Array<{
 *     cell: object|null, rowIdx: number, cellIdx: number,
 *     column: number, colSpan: number, line: number, rowSpan: number,
 *   }>,
 * }} Where `column` and `line` are 1-based grid positions, in the order the
 *   cells come in — row by row.
 */
export const layoutGridRows = (
  rows = [],
  { columns, fillLines = false } = {},
) => {
  const placed = [];
  // The cells of earlier rows still spanning down: the rows they have left.
  let spanning = [];
  let line = 1;

  rows.forEach((row, rowIdx) => {
    const rowsLeft = rows.length - rowIdx;
    const spanningColumns = spanning.reduce((sum, s) => sum + s.columns, 0);
    const ownColumns = row.reduce((sum, cell) => sum + getCellColSpan(cell), 0);

    // The columns this row's own cells share — the rest are the spanning
    // cells'.
    const lineColumns = columns
      ? columns
      : Math.max(1, Math.min(ownColumns, MAX_ROW_COLUMNS - spanningColumns));
    const freeWidth = columns
      ? ONE
      : spanning.reduce((free, s) => sub(free, s.width), ONE);
    const unit = div(freeWidth, lineColumns);

    // This row's own cells spanning down extend through its extra lines too.
    let rowSpanning = [];
    let segments = getFreeSegments(spanning);
    let segmentIdx = 0;
    let cursor = segments[0]?.start ?? ZERO;
    const rowStartLine = line;

    // The line's last cell and where its free stretch ends, for `fillLines`.
    let lineLast = null;

    const fillLine = () => {
      if (fillLines && lineLast?.entry.cell) {
        lineLast.entry.width = sub(lineLast.end, lineLast.entry.start);
      }

      lineLast = null;
    };

    const newLine = () => {
      fillLine();
      line += 1;
      segments = getFreeSegments([...spanning, ...rowSpanning]);
      segmentIdx = 0;
      cursor = segments[0]?.start ?? ZERO;
    };

    row.forEach((cell, cellIdx) => {
      const width = mul(unit, Math.min(getCellColSpan(cell), lineColumns));

      const fits = () =>
        segmentIdx < segments.length &&
        cmp(add(cursor, width), segments[segmentIdx].end) <= 0;

      // Into the first free stretch it fits, else onto a new line.
      while (!fits() && segmentIdx < segments.length) {
        segmentIdx += 1;
        cursor = segments[segmentIdx]?.start ?? ZERO;
      }

      let cellWidth = width;

      if (!fits()) {
        newLine();

        // Too wide for any free stretch even on a line of its own: it takes
        // the widest one.
        if (!fits()) {
          segmentIdx = segments.reduce(
            (best, segment, idx) =>
              cmp(
                sub(segment.end, segment.start),
                sub(segments[best].end, segments[best].start),
              ) > 0
                ? idx
                : best,
            0,
          );
          cursor = segments[segmentIdx]?.start ?? ZERO;
          cellWidth = segments[segmentIdx]
            ? sub(segments[segmentIdx].end, segments[segmentIdx].start)
            : ONE;
        }
      }

      const entry = {
        cell,
        rowIdx,
        cellIdx,
        start: cursor,
        width: cellWidth,
        line,
        lines: 1,
      };

      placed.push(entry);
      cursor = add(cursor, cellWidth);
      lineLast = { entry, end: segments[segmentIdx]?.end ?? ONE };

      const rowSpan = Math.min(getCellRowSpan(cell), rowsLeft);

      if (cell && rowSpan > 1) {
        rowSpanning.push({
          entry,
          // Read from the entry, which `fillLines` may still stretch.
          get start() {
            return entry.start;
          },
          get width() {
            return entry.width;
          },
          // Its share of the columns, as the rows below count them.
          columns: Math.min(getCellColSpan(cell), lineColumns),
          rowsLeft: rowSpan - 1,
        });
      }
    });

    fillLine();

    // Whatever spans this row covers all of its lines.
    const rowEndLine = line;

    rowSpanning.forEach(({ entry }) => {
      entry.lines = rowEndLine - entry.line + 1;
    });

    spanning.forEach((s) => {
      s.entry.lines += rowEndLine - rowStartLine + 1;
      s.rowsLeft -= 1;
    });

    spanning = [...spanning, ...rowSpanning].filter((s) => s.rowsLeft > 0);

    line = rowEndLine + 1;
  });

  // Fixed columns are tracks of their own (a subgrid's come from its parent),
  // so positions are always counted in them, or finer.
  const gridColumns = placed.reduce(
    (result, { start, width }) => lcm(lcm(result, start[1]), width[1]),
    columns ?? 1,
  );

  return {
    columns: gridColumns,
    lines: line - 1,
    cells: placed.map(
      ({ cell, rowIdx, cellIdx, start, width, line, lines }) => ({
        cell,
        rowIdx,
        cellIdx,
        column: (start[0] * gridColumns) / start[1] + 1,
        colSpan: (width[0] * gridColumns) / width[1],
        line,
        rowSpan: lines,
      }),
    ),
  };
};
